import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { configureApiService, createAuthStatusFetch, createTimeoutFetch, setWriteTimeoutGate } from "@em/api";
import { anyOverlayOpen, onOverlayStackChange } from "@em/ui";

import { appEnv } from "./branding";
import { APOLLO_TYPE_POLICIES } from "./apolloTypePolicies";

// The non-account crew session token (docs/CREW-ACCESS.md, scenario 3). Read here
// directly (not via CrewSessionService) to keep this transport module dependency-free.
const CREW_TOKEN_KEY = "eventsmagic_crew_token";
const readCrewToken = (): string | null => {
  try {
    return localStorage.getItem(CREW_TOKEN_KEY);
  } catch {
    return null;
  }
};

// Get the active brand's space ID (brand-aware; see config/branding.ts).
export const getSpaceId = () => {
  const spaceId = appEnv.spaceId;
  if (import.meta.env.DEV) {
    console.log("Space ID configured:", spaceId);
  }
  return spaceId;
};

/**
 * ROADMAP #122 — how long a MUTATION may take before it is abandoned and reported as "no answer". A drawer locks every
 * way out while it saves (#121), so without a limit a request that never answered held it — and the work in it — until
 * a reload. Generous on purpose: the slowest real saves (a Stripe refund, generating 500 tickets, running the voucher
 * rules over a big guest list) take seconds, and abandoning one the server then finishes is worse than waiting.
 */
export const MUTATION_TIMEOUT_MS = 45_000;

/**
 * …and a READ (see @em/api `requestTimeout.ts`): longer, because a read is safe to wait for, but bounded — some saves
 * await a read inside their locked window. (An edit drawer no longer locks while its record loads — ROADMAP #131.)
 */
export const READ_TIMEOUT_MS = 90_000;

/**
 * Mutations allowed longer than `MUTATION_TIMEOUT_MS`, by operation name — for work that grows with what the user sent.
 *
 * ⚠ EMPTY since ROADMAP #132: the guest import (the reason this list existed) now runs on the server in the background —
 *   `EventGuestImportStart` answers as soon as the list is stored, and the review (`EventGuestImportPlan`) covers at
 *   most 200 rows. Before adding a name here, ask whether the work belongs in a background job instead.
 */
export const SLOW_MUTATIONS_MS: Readonly<Record<string, number>> = {};

// Create HTTP link with credentials to include cookies
const httpLink = createHttpLink({
  uri: appEnv.graphqlEndpoint,
  // A request that never answers ends as an ordinary refusal (OpResult NETWORK_ERROR), so the drawer lets go; an HTTP 401
  // reaches the one guarded sign-out handler whatever its body type (Apollo 4 hides the status of a GraphQL-typed one).
  fetch: createTimeoutFetch({
    mutationTimeoutMs: MUTATION_TIMEOUT_MS,
    readTimeoutMs: READ_TIMEOUT_MS,
    slowMutationsMs: SLOW_MUTATIONS_MS,
    fetchImpl: createAuthStatusFetch(),
  }),
  credentials: "include", // Important: This includes HTTP-only cookies
  headers: {
    "x-space-id": getSpaceId(),
  },
});

// Attach the crew-session token (if any) per-request. A static header on
// createHttpLink would freeze the value at module load; setContext re-reads
// localStorage on every operation, so a crew sign-in / sign-out takes effect
// immediately. Only sends the header when a token exists (never an empty string).
const crewAuthLink = setContext((_operation, prevContext) => {
  const crewToken = readCrewToken();
  if (!crewToken) {
    return prevContext;
  }
  return {
    ...prevContext,
    headers: {
      ...(prevContext.headers || {}),
      "x-crew-token": crewToken,
    },
  };
});

// Error link: LOGS what went wrong, and nothing else.
// ⚠ It does not sign anyone out. It used to: on an HTTP 401 it wiped the stored user and sent the browser to
// /account/signin — written for Apollo 3 (`graphQLErrors` / `networkError`), which Apollo 4 never sets, so it never ran.
// Revived as it stood it would have been a SECOND sign-out path past the guarded one — no `me()` check, so a refusal
// would end a good session. Signing out belongs to `ApiService` → `triggerAuthError` → the handler in App.tsx (it skips
// a crew session and asks `me()` first). ▶ check-auth-errors
const errorLink = onError(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, path }) => {
      console.error(`GraphQL error in ${operation.operationName}: ${message}${path ? ` (path: ${path.join(".")})` : ""}`);
    });

    return;
  }

  console.error(`Network error in ${operation.operationName}: ${error}`);
});

// Create Apollo Client
export const apolloClient = new ApolloClient({
  link: from([errorLink, crewAuthLink, httpLink]),
  // ⚠ A value type with an `id` (not a record) needs `keyFields: false` there — see the module.
  cache: new InMemoryCache({ typePolicies: APOLLO_TYPE_POLICIES }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
    },
    query: {
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
});

// Inject this app's Apollo client (and session teardown) into the shared
// @em/api service layer, once, at module load — before any query runs.
configureApiService({
  client: apolloClient,
  onClearSession: () => localStorage.removeItem("eventsmagic_user"),
  restApiUrl: appEnv.apiUrl,
  spaceId: appEnv.spaceId,
});

// ROADMAP #131 — a save that timed out makes the lists re-read, but only once no drawer / modal is open: the drawer is
// still showing the message about that save, and a list read would clear it (or re-seed a form being edited).
setWriteTimeoutGate({ isClear: () => !anyOverlayOpen(), onChange: onOverlayStackChange });
